import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Calendar, Clock, User, Building } from 'lucide-react';
import { Report, Customer } from '@/types';
import { useAppContext } from '@/contexts/AppContext';
import ReportDetailDialog from './ReportDetailDialog';

interface CalendarViewProps {
  reports: Report[];
  customers: Customer[];
}

const CalendarView: React.FC<CalendarViewProps> = ({ reports, customers }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { currentUser } = useAppContext();

  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const reportsForMonth = useMemo(() => {
    return reports.filter(report => {
      if (!report.date) return false;
      const reportDate = new Date(report.date);
      return reportDate.getMonth() === currentMonth && reportDate.getFullYear() === currentYear;
    });
  }, [reports, currentMonth, currentYear]);

  const getReportsForDay = (day: number) => {
    return reportsForMonth.filter(report => {
      const reportDate = new Date(report.date);
      return reportDate.getDate() === day;
    });
  };

  const getCustomerName = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.businessName || 'Unknown Customer';
  };

  const handleReportClick = (report: Report) => {
    setSelectedReport(report);
    setIsDialogOpen(true);
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const renderCalendarDays = () => {
    const days = [];
    
    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="h-24"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayReports = getReportsForDay(day);
      const isToday = new Date().toDateString() === new Date(currentYear, currentMonth, day).toDateString();
      
      days.push(
        <div key={day} className={`h-24 border border-gray-200 p-1 ${isToday ? 'bg-blue-50' : ''}`}>
          <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : ''}`}>
            {day}
          </div>
          <div className="space-y-1 overflow-y-auto max-h-16">
            {dayReports.map((report, index) => (
              <div 
                key={report.id} 
                className="text-xs bg-blue-100 text-blue-800 p-1 rounded truncate cursor-pointer hover:bg-blue-200 transition-colors"
                onClick={() => handleReportClick(report)}
              >
                <div className="font-medium">{getCustomerName(report.customerId)}</div>
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {report.time}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return days;
  };

  const selectedCustomer = selectedReport ? customers.find(c => c.id === selectedReport.customerId) : null;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Sales Calendar
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => navigateMonth('prev')}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="font-medium min-w-[140px] text-center">
                {monthNames[currentMonth]} {currentYear}
              </span>
              <Button variant="outline" size="sm" onClick={() => navigateMonth('next')}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-0 border border-gray-200">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="bg-gray-50 p-2 text-center font-medium text-sm border-b border-gray-200">
                {day}
              </div>
            ))}
            {renderCalendarDays()}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Upcoming Reports</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {reportsForMonth
              .filter(report => new Date(report.date) >= new Date())
              .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
              .slice(0, 5)
              .map(report => {
                const customer = customers.find(c => c.id === report.customerId);
                return (
                  <div 
                    key={report.id} 
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleReportClick(report)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex flex-col">
                        <div className="font-medium">{customer?.businessName}</div>
                        <div className="text-sm text-gray-600 flex items-center gap-2">
                          <Calendar className="h-3 w-3" />
                          {new Date(report.date).toLocaleDateString()}
                          <Clock className="h-3 w-3" />
                          {report.time}
                        </div>
                      </div>
                    </div>
                    <Badge variant="outline">{report.businessType}</Badge>
                  </div>
                );
              })}
            {reportsForMonth.filter(report => new Date(report.date) >= new Date()).length === 0 && (
              <p className="text-gray-500 text-center py-4">No upcoming reports this month</p>
            )}
          </div>
        </CardContent>
      </Card>

      <ReportDetailDialog
        report={selectedReport}
        customer={selectedCustomer || null}
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      />
    </div>
  );
};

export default CalendarView;